import React, { useState, useRef, useEffect } from 'react';
import { ReactComponent as ArrowDownIcon } from '../../../../assets/icons/arrow-down.svg';
import AutoSaveIndicator from '../../../../composants/AutoSaveIndicator/AutoSaveIndicator';
import type { AutoSaveStatus } from '../../../../hooks/useAutoSave';

// ── Types ─────────────────────────────────────────────────────────────────────

interface LotDisponible {
  id: number;
  region: boolean;
  departement?: {
    id: number;
    nom: string;
    id_region?: number;
    region?: {
      id: number;
      nom: string;
    };
  };
}

interface SectionChoixLotsProps {
  lotTechnique: { id: number; code: string; nom: string; cpv?: string };
  lotsDisponibles: LotDisponible[];
  selectedIds: number[];
  canEdit: boolean;
  onLotsGeoChange: (ids: number[]) => void;
  autoSaveStatus: AutoSaveStatus;
  autoSaveError: string | null;
}

// ── Section "Choix des lots" ──────────────────────────────────────────────────

const SectionChoixLots: React.FC<SectionChoixLotsProps> = ({
  lotTechnique,
  lotsDisponibles,
  selectedIds,
  canEdit,
  onLotsGeoChange,
  autoSaveStatus,
  autoSaveError,
}) => {
  const lotCode = Number(lotTechnique.code);
  const isRegional = lotCode >= 100 && lotCode < 200;

  // Pour 1xx : regrouper les lots disponibles par région
  const regionGroups = React.useMemo(() => {
    if (!isRegional) return [];
    const map = new Map<number, { regionId: number; regionNom: string; lots: LotDisponible[] }>();
    lotsDisponibles.forEach((lot) => {
      const regionId = lot.departement?.region?.id ?? lot.departement?.id ?? 0;
      const regionNom = lot.departement?.region?.nom ?? lot.departement?.nom ?? 'Autre';
      if (!map.has(regionId)) map.set(regionId, { regionId, regionNom, lots: [] });
      map.get(regionId)!.lots.push(lot);
    });
    return Array.from(map.values()).sort((a, b) => a.regionNom.localeCompare(b.regionNom));
  }, [isRegional, lotsDisponibles]);

  const handleToggle = (lotId: number) => {
    if (!canEdit) return;
    onLotsGeoChange(
      selectedIds.includes(lotId)
        ? selectedIds.filter((id) => id !== lotId)
        : [...selectedIds, lotId],
    );
  };

  const handleToggleRegion = (regionLots: LotDisponible[]) => {
    if (!canEdit) return;
    const ids = regionLots.map((l) => l.id);
    const allSelected = ids.every((id) => selectedIds.includes(id));
    onLotsGeoChange(
      allSelected
        ? selectedIds.filter((id) => !ids.includes(id))
        : Array.from(new Set([...selectedIds, ...ids])),
    );
  };

  const handleToggleAll = () => {
    if (!canEdit) return;
    onLotsGeoChange(
      selectedIds.length === lotsDisponibles.length && lotsDisponibles.length > 0
        ? []
        : lotsDisponibles.map((l) => l.id),
    );
  };

  const selectedLots = lotsDisponibles.filter((l) => selectedIds.includes(l.id));
  const lotTechLabel = `Lot ${lotTechnique.code} — ${lotTechnique.nom}`;

  const [lotsDropdownOpen, setLotsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!lotsDropdownOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setLotsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [lotsDropdownOpen]);

  // Résumé pour le bouton dropdown
  const dropdownSummary = isRegional
    ? (() => {
      const selRegions = regionGroups.filter(({ lots: rl }) => rl.every((l) => selectedIds.includes(l.id)));
      return selRegions.length > 0
        ? `${selRegions.length} région(s) sélectionnée(s)`
        : 'Sélectionner les régions';
    })()
    : selectedLots.length > 0
      ? `${selectedLots.length} département(s) sélectionné(s)`
      : 'Sélectionner les départements';

  return (
    <>
      <div className="section-header">Choix des lots</div>
      <div className='section-content section-content--overflow-visible'>


        {/* Lot technique — lecture seule */}
        <div className="choix-lots-field">
          <label className="field-label">
            Choix de la catégorie technique suivante (1 choix possible uniquement)
          </label>
          <select
            className="select-field select-disabled"
            value={lotTechnique.id}
            disabled
          >
            <option value={lotTechnique.id}>{lotTechLabel}</option>
          </select>
        </div>

        {/* Lots géographiques */}
        <div className="choix-lots-field">
          <label className="field-label">
            Choix du ou des lots géographiques{isRegional ? ' (par région)' : ' (par département)'}
          </label>

          {lotsDisponibles.length === 0 ? (
            <p style={{ color: '#9ca3af', fontSize: '1.3rem' }}>Aucun lot disponible.</p>
          ) : (
            <div className="lots-dropdown" ref={dropdownRef}>
              <button
                type="button"
                className="lots-dropdown-trigger"
                onClick={() => setLotsDropdownOpen((o) => !o)}
              >
                <span>{dropdownSummary}</span>
                <ArrowDownIcon className={`accordion-chevron${lotsDropdownOpen ? ' accordion-chevron--open' : ''}`} />
              </button>

              {lotsDropdownOpen && (
                <div className={`lots-geo-list${!canEdit ? ' lots-geo-list-disabled' : ''}`}>
                  {/* Tout sélectionner */}
                  {isRegional ? (
                    <label className="lot-geo-option lot-geo-option-all">
                      <input
                        type="checkbox"
                        checked={regionGroups.length > 0 && regionGroups.every(({ lots: rl }) => rl.every((l) => selectedIds.includes(l.id)))}
                        ref={(el) => {
                          if (el) {
                            const total = regionGroups.length;
                            const sel = regionGroups.filter(({ lots: rl }) => rl.every((l) => selectedIds.includes(l.id))).length;
                            el.indeterminate = sel > 0 && sel < total;
                          }
                        }}
                        onChange={handleToggleAll}
                        disabled={!canEdit}
                      />
                      <span>Tous ({regionGroups.length})</span>
                    </label>
                  ) : (
                    <label className="lot-geo-option lot-geo-option-all">
                      <input
                        type="checkbox"
                        checked={lotsDisponibles.length > 0 && selectedIds.length === lotsDisponibles.length}
                        ref={(el) => { if (el) el.indeterminate = selectedIds.length > 0 && selectedIds.length < lotsDisponibles.length; }}
                        onChange={handleToggleAll}
                        disabled={!canEdit}
                      />
                      <span>Tous ({lotsDisponibles.length})</span>
                    </label>
                  )}

                  {isRegional ? (
                    regionGroups.map(({ regionId, regionNom, lots: regionLots }) => (
                      <label key={regionId} className="lot-geo-option">
                        <input
                          type="checkbox"
                          checked={regionLots.every((l) => selectedIds.includes(l.id))}
                          onChange={() => handleToggleRegion(regionLots)}
                          disabled={!canEdit}
                        />
                        <span>{regionNom}</span>
                      </label>
                    ))
                  ) : (
                    lotsDisponibles.map((lot) => (
                      <label key={lot.id} className="lot-geo-option">
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(lot.id)}
                          onChange={() => handleToggle(lot.id)}
                          disabled={!canEdit}
                        />
                        <span>{lot.departement?.nom ?? `Lot ${lot.id}`}</span>
                      </label>
                    ))
                  )}
                </div>
              )}
            </div>
          )}

          {/* Compteur + Chips */}
          {isRegional ? (() => {
            const selectedRegions = regionGroups.filter(({ lots: rl }) => rl.every((l) => selectedIds.includes(l.id)));
            return (
              <>
                {selectedRegions.length > 0 && (
                  <p className="lots-count">{selectedRegions.length} région(s) sélectionnée(s)</p>
                )}
                <div className="lots-chips">
                  {selectedRegions.map(({ regionId, regionNom, lots: regionLots }) => (
                    <span key={regionId} className="lot-chip">
                      {regionNom}
                      {canEdit && (
                        <button
                          className="lot-chip-remove"
                          onClick={() => handleToggleRegion(regionLots)}
                          aria-label={`Retirer ${regionNom}`}
                        >
                          {'×'}
                        </button>
                      )}
                    </span>
                  ))}
                </div>
              </>
            );
          })() : (
            <>
              {selectedLots.length > 0 && (
                <p className="lots-count">{selectedLots.length} lot(s) sélectionné(s)</p>
              )}
              <div className="lots-chips">
                {selectedLots.map((lot) => (
                  <span key={lot.id} className="lot-chip">
                    {lot.departement?.nom ?? `Lot ${lot.id}`}
                    {canEdit && (
                      <button
                        className="lot-chip-remove"
                        onClick={() => handleToggle(lot.id)}
                        aria-label={`Retirer ${lot.departement?.nom ?? ''}`}
                      >
                        {'×'}
                      </button>
                    )}
                  </span>
                ))}
              </div>
            </>
          )}

          {/* Indicateur auto-save (Story 13.2) */}
          <div style={{ marginTop: '0.75rem' }}>
            <AutoSaveIndicator status={autoSaveStatus} error={autoSaveError} />
          </div>
        </div>
      </div>
    </>
  );
};

export default SectionChoixLots;
